/**
 * http-error-parser-message.ts
 * -----------------------------------------------------------------------------
 * General frontend HTTP error parser.
 *
 * This module takes a wide variety of error payloads thrown by the API / HTTP
 * clients and normalises them into a single, human-friendly error message plus
 * structured field errors that can be passed directly to the user (e.g. toast /
 * form-level messaging).
 *
 * The BNP API can return several error shapes (and several generic-looking
 * `message` fields), so this parser defends against them all:
 *
 *   1. Axios error    ->  error.response.data  (detail lives under .response)
 *   2. Fetch error    ->  the thrown parsed body
 *   3. BaseError/JSON ->  { status, message, errors, stack }
 *      - `message` can be generic ("Validation Error", "Bad Request",
 *        "Internal Server Error", the exception class name, etc.)
 *      - the real information may live in `errors` (array of strings, array of
 *        objects, or a { field: [messages] } map)
 *   4. Native Error   ->  name + message ("TypeError", "BadRequestError", ...)
 *   5. yup ValidationError shape  (errors: string[])
 *   6. zod error shape            (issues: [{ message }])
 *   7. primitives (string / number)
 *
 * Usage:
 *   const err = parseHttpError(error);
 *   toast.error(err.message);            // single, human-friendly message
 *   form.setErrors(err.fieldErrors);     // { field: "first message" }
 *   console.log(err.status, err.statusText);
 * -----------------------------------------------------------------------------
 */

/** Generic messages that carry no user value — detail is elsewhere. */
const GENERIC_MESSAGES: ReadonlySet<string> = new Set([
  "validation",
  "validation error",
  "validation failed",
  "validationexception",
  "bad request",
  "badrequest",
  "badrequesterror",
  "error",
  "internal server error",
  "internal error",
  "request failed with status code",
  "network error",
  "failed to fetch",
  "unhandled exception",
  "unknown error",
  "unprocessable entity",
  "an error occurred",
  "something went wrong",
]);

import type { FlatFieldError, ParsedError } from "./http-error-types";

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isPlainObject = (value: unknown): boolean => {
  if (!isObject(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const toString = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  return "";
};

const looksGeneric = (message: string): boolean => {
  if (!message) return true;
  const normalized = message.trim().toLowerCase();
  return GENERIC_MESSAGES.has(normalized);
};

/** Reduce an axios-style technical fragment to nothing so fallbacks win. */
const cleanValue = (value: unknown): string => {
  const raw = toString(value);
  if (!raw) return "";
  if (/request failed with status code/i.test(raw)) return "";
  return raw;
};

/** Flatten any list-like value into human message strings. */
function flattenValues(value: unknown, out: string[] = []): string[] {
  if (value == null) return out;
  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === "string") {
        const cleaned = cleanValue(item);
        if (cleaned) out.push(cleaned);
      } else if (isObject(item)) {
        const msg =
          cleanValue(item.message) ||
          cleanValue(item.msg) ||
          cleanValue(item.reason) ||
          cleanValue(item.detail);
        if (msg) out.push(msg);
      }
    }
    return out;
  }
  if (typeof value === "string") {
    const cleaned = cleanValue(value);
    if (cleaned) out.push(cleaned);
  } else if (isObject(value)) {
    for (const key of Object.keys(value)) {
      flattenValues(value[key], out);
    }
  }
  return out;
}

/** Build a stable list of free-form error strings from an arbitrary payload. */
function collectMessageList(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) return flattenValues(value);
  if (typeof value === "string")
    return cleanValue(value) ? [cleanValue(value)] : [];
  if (typeof value === "number") return [String(value)];

  if (isObject(value)) {
    // yup ValidationError
    if (Array.isArray(value.errors)) return flattenValues(value.errors);
    // zod — issues: [{ message }]
    if (Array.isArray(value.issues)) return flattenValues(value.issues);

    const collected: string[] = [];
    for (const key of Object.keys(value)) {
      if (Array.isArray(value[key]) || typeof value[key] === "string") {
        collected.push(...flattenValues(value[key]));
      } else if (isPlainObject(value[key])) {
        const sub = collectMessageList(value[key]);
        collected.push(...sub);
      }
    }
    return collected;
  }
  return [];
}

/** Detect { field: ["msg", ...] } / { field: "msg" } maps and return keyed errors. */
function extractFieldMap(value: any): Record<string, string[]> | null {
  if (!isPlainObject(value)) return null;
  const keys = Object.keys(value);
  if (keys.length === 0) return null;
  const excluded =
    /^(status|message|errors|error|detail|stack|code|ok|timestamp|path|method|duration|success)$/i;
  const looksLikeMap = keys.every(
    (key) =>
      !excluded.test(key) &&
      (Array.isArray(value[key]) || typeof value[key] === "string") &&
      value[key] != null,
  );
  if (!looksLikeMap) return null;

  const map: Record<string, string[]> = {};
  for (const key of keys) {
    const messages = flattenValues(value[key]);
    if (messages.length) map[key] = messages;
  }
  return Object.keys(map).length ? map : null;
}

function flattenFieldMap(map: Record<string, string[]>): FlatFieldError[] {
  return Object.entries(map)
    .filter(([, messages]) => messages.length)
    .map(([field, messages]) => ({ field, messages }));
}

/** Choose the best, non-generic candidate message in order of preference. */
function bestMessage(candidates: string[]): string {
  for (const candidate of candidates) {
    if (candidate && !looksGeneric(candidate)) return candidate;
  }
  return candidates.find((c) => c) ?? "";
}

const HTTP_STATUS_TEXT: Record<number, string> = {
  400: "Bad Request",
  401: "Unauthorized",
  403: "Forbidden",
  404: "Not Found",
  409: "Conflict",
  422: "Validation Failed",
  429: "Too Many Requests",
  500: "Internal Server Error",
  502: "Bad Gateway",
  503: "Service Unavailable",
  504: "Gateway Timeout",
};

const statusText = (status: number | null): string =>
  status == null ? "" : (HTTP_STATUS_TEXT[status] ?? "");

/**
 * Turn any captured error into a friendly message plus structured field errors.
 */
export function parseHttpError(input: unknown): ParsedError {
  // Fast-path primitives
  if (typeof input === "string") {
    return {
      message: input || "An unknown error occurred",
      status: null,
      statusText: "",
      fieldErrors: {},
      fieldErrorList: [],
    };
  }
  if (typeof input === "number") {
    return {
      message: String(input),
      status: input,
      statusText: statusText(input),
      fieldErrors: {},
      fieldErrorList: [],
    };
  }
  if (!isObject(input)) {
    return {
      message: "An unknown error occurred",
      status: null,
      statusText: "",
      fieldErrors: {},
      fieldErrorList: [],
    };
  }

  // Unwrap axios-style wrapper: { response: { data, status, statusText } } (or { data })
  let body: unknown = input;
  let status: number | null = null;
  let statusLabel = "";

  if (isObject(input.response)) {
    const response = input.response;
    if (typeof response.status === "number") status = response.status;
    statusLabel = toString(response.statusText);
    if (isObject(response.data)) {
      body = response.data;
    } else if (response.data !== undefined) {
      body = response.data;
    }
  } else if (isObject(input.data)) {
    body = input.data;
  }

  const raw = body as Record<string, unknown> | null;

  // Derive status code from the body when not already set
  if (raw) {
    if (typeof raw.statusCode === "number") status = raw.statusCode;
    else if (typeof raw.status === "number") status = raw.status;
  }

  // 1. Native / thrown JS errors where only name + message are meaningful
  if (raw && typeof raw.name === "string" && /Error$/i.test(raw.name)) {
    const msg = cleanValue(raw.message);
    return {
      message: msg || "An unexpected error occurred",
      status,
      statusText: statusLabel || statusText(status),
      fieldErrors: {},
      fieldErrorList: [],
      raw: body,
    };
  }

  // 2. The friendly `message` field, if informative
  const bodyMessage = isObject(raw) ? cleanValue(raw.message) : "";

  // 3. Detail extracted from `errors` / `issues` / `detail` / `error`
  let detailMessages: string[] = [];
  let fieldMap: Record<string, string[]> | null = null;

  if (isObject(raw)) {
    if (raw.errors !== undefined) {
      fieldMap = extractFieldMap(raw.errors);
      detailMessages = fieldMap
        ? flattenFieldMap(fieldMap).flatMap((entry) => entry.messages)
        : collectMessageList(raw.errors);
    } else if (raw.issues !== undefined) {
      detailMessages = collectMessageList(raw.issues);
    } else if (raw.detail !== undefined) {
      detailMessages = collectMessageList(raw.detail);
    } else if (raw.error !== undefined && raw.error !== raw.message) {
      detailMessages = collectMessageList(raw.error);
    }
  }

  // 4. Build the final user-facing message: prefer informative `message`,
  //    otherwise fall back to extracted detail messages.
  const messageDraft =
    bestMessage([bodyMessage, ...detailMessages]) ||
    bodyMessage ||
    detailMessages[0] ||
    "Something went wrong. Please try again.";

  const message = messageDraft || "Something went wrong. Please try again.";

  // 5. Rebuild per-field maps for form wiring.
  const fieldErrorList = flattenFieldMap(fieldMap ?? {});
  const fieldErrors = fieldErrorList.reduce<Record<string, string>>(
    (acc, entry) => {
      if (entry.field) acc[entry.field] = entry.messages.join(", ");
      return acc;
    },
    {},
  );

  const seen = new Set<string>();
  const rawMessages = [
    ...detailMessages,
    ...flattenFieldMap(fieldMap ?? {}).flatMap((e) => e.messages),
  ].filter((m) => {
    if (!m || seen.has(m)) return false;
    seen.add(m);
    return true;
  });

  return {
    message,
    status,
    statusText: statusLabel || statusText(status),
    fieldErrors,
    fieldErrorList,
    rawMessages,
    raw: body,
  };
}

/**
 * Curated, human-friendly message for an error — ideal for a toast / alert.
 */
export function getErrorMessage(error: unknown): string {
  return parseHttpError(error).message;
}

/**
 * Field-keyed validation errors, wired directly into a form.
 * Pass `fields` to keep only specific keys.
 */
export function getFieldErrors(
  error: unknown,
  fields?: string[],
): Record<string, string> {
  const parsed = parseHttpError(error);
  if (!fields || fields.length === 0) return parsed.fieldErrors;
  const filtered: Record<string, string> = {};
  for (const field of fields) {
    if (parsed.fieldErrors[field] != null)
      filtered[field] = parsed.fieldErrors[field];
  }
  return filtered;
}

/**
 * The HTTP status code, if it could be determined.
 */
export function getErrorStatus(error: unknown): number | null {
  return parseHttpError(error).status;
}

/** Types are re-exported for convenience. */
export type { FlatFieldError, ParsedError };

export default parseHttpError;
